import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { CommonButtonRowWidget, UtilityService } from "@eforall/common";
import { DialogService } from "@eforall/dashboard";
import { LanguageId, VolunteerDetails, VolunteerInterestRolesName } from "@interfaces";
import { DomainDataService } from "../../../../../../services";
import { PersonDialog } from "../../../../person-dialog";
import { VideoDialog, VideoDialogData } from "../../../video-dialog/video.dialog";
import { VolunteerDialogDataService } from "../../volunteer-dialog-data.service";


@Component({
	selector: 'volunteer-overview-content-dialog-part',
	standalone: true,
	imports: [
		CommonButtonRowWidget,
	],
	templateUrl: './volunteer-overview-content-dialog.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './volunteer-overview-content-dialog.part.scss'
})
export class VolunteerOverviewContentDialogPart {

	private readonly dialogService = inject(DialogService);
	private readonly domainData = inject(DomainDataService);
	private readonly dialogDataService = inject(VolunteerDialogDataService);
	private readonly util = inject(UtilityService);

	// Expose language IDs for template use
	public readonly englishLanguageId = LanguageId.English;
	public readonly spanishLanguageId = LanguageId.Espanol;

	public readonly volunteerDetails = computed(() => {
		const volunteer = this.dialogDataService.volunteerDetails();
		if (!volunteer) return null;

		const languages: string[] = [];
		if (volunteer.canVolunteerInEnglish) languages.push('English');
		if (volunteer.canVolunteerInSpanish) languages.push('Spanish');

		const interests: string[] = [];
		if (volunteer.isMentorApproved) interests.push(VolunteerInterestRolesName.IsMentorApproved);
		if (volunteer.isInClassApproved) interests.push(VolunteerInterestRolesName.IsInClassApproved);
		if (volunteer.isOfficeHoursApproved) interests.push(VolunteerInterestRolesName.IsOfficeHoursApproved);

		const expertises: string[] = volunteer.expertises
			.map(e => this.domainData.data().expertisesMap[e]?.name)
			.filter(name => typeof name === 'string');

		const socialLinks = volunteer.socialLinks
			.map(link => ({
				webLinkTypeId: link.webLinkTypeId,
				type: this.domainData.data().webLinkTypesMap[link.webLinkTypeId]?.name,
				url: link.url
			}))

		const topics: string[] = volunteer.volunteerTopics.filter(t => t.isApproved)
			.reduce((a: string[], e) => {
				const topic = this.domainData.data().topicsMap[e.topicId];
				if (topic && topic.enabled && topic.needsSpecialist) {
					a.push(topic.name);
				}
				return a;
			}, []);


		return {
			...volunteer,
			languages,
			interests,
			expertises,
			socialLinks,
			topics,
			leadVolunteerCreatedUTC: this.util.date.formatUTC(volunteer.leadVolunteerCreatedUTC, 'MMM, DD YYYY', 'No Time', 'en-US'),
			interviewDateUTC: volunteer.interviewDateUTC ? this.util.date.formatUTC(volunteer.interviewDateUTC, 'MMM, DD YYYY', 'H:MM AM EST', 'en-US') : "Not Scheduled",
			status: this.domainData.data().volunteerStatusesMap[volunteer.volunteerStatusId]?.name,
			englishVideoStatus: this.getEnglishVideoStatus(volunteer),
			spanishVideoStatus: this.getSpanishVideoStatus(volunteer),
		};
	});

	public showLeadDetails() {
		this.dialogDataService.overviewContentView.set('lead');
	}

	async openVideoDialog(languageId: LanguageId) {
		const details = this.volunteerDetails();
		if (!details?.person.id) return;

		const hasVideo = languageId === LanguageId.English ? details.hasEnVideo : details.hasEsVideo;
		if (hasVideo) {
			const dialogData: VideoDialogData = {
				type: 'volunteer',
				personId: details.person.id,
				uniqueVideoId: languageId
			};
			this.dialogService.showCustom(VideoDialog, { data: dialogData }, 500, 550);
		}
	}

	async openPersonDialog(personId: number) {
		await this.dialogService.showCustom(PersonDialog, { data: { personId, toEdit: false } }, 800, 800);
	}

	private formatVideoStatus(hasVideo: boolean, uploadedUTC: number | undefined): string {
		if (!hasVideo) return 'Not Added';
		if (hasVideo && !uploadedUTC) return 'Uploaded';
		if (hasVideo && uploadedUTC) {
			const formattedDate = this.util.date.formatUTC(uploadedUTC, 'MMM, DD YYYY', 'H:MM AM EST', 'en-US');
			return `Uploaded: ${formattedDate}`;
		}
		return 'Not Added';
	}

	private getEnglishVideoStatus(volunteer: VolunteerDetails): string {
		return this.formatVideoStatus(volunteer.hasEnVideo, volunteer.englishVideoUploadedUTC);
	}

	private getSpanishVideoStatus(volunteer: VolunteerDetails): string {
		return this.formatVideoStatus(volunteer.hasEsVideo, volunteer.spanishVideoUploadedUTC);
	}
}